import { AlertTriangle } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { Callout, DataTable, type Column } from '../../components/ds';
import { status, chip, btnSecondary } from '../../components/mono/primitives';

/**
 * Foundations — the four rules that are easy to break silently.
 *
 * These were load-bearing and lived ONLY in code comments
 * (mono/primitives.ts, ds/foundation.ts). Anyone who did not read the source
 * re-broke them, and each has been broken by code that looked correct and
 * passed lint, typecheck and build. A rule that exists only where the person
 * about to violate it will not look is not documented.
 */

interface RuleRow {
  property: string;
  mechanism: string;
  why: string;
}

const CASCADE_ROWS: RuleRow[] = [
  {
    property: 'border-color (non-default)',
    mechanism: 'inline style',
    why: '.dark * { border-color: var(--rule) } ties with a plain border class and comes later, so it wins.',
  },
  {
    property: 'h1–h6 colour',
    mechanism: 'inline style',
    why: '.dark h1…h6 { color: var(--text-pri) } outweighs a plain text class.',
  },
  {
    property: 'background',
    mechanism: 'token class',
    why: 'Neither rule touches it.',
  },
  {
    property: 'text colour (non-heading)',
    mechanism: 'token class',
    why: 'Neither rule touches it.',
  },
  {
    property: 'hover / focus states',
    mechanism: 'token class',
    why: 'Pseudo-classes cannot be expressed inline at all — this half must stay in Tailwind.',
  },
];

const CASCADE_COLUMNS: Column<RuleRow>[] = [
  { key: 'property', header: 'What you are setting', width: '28%' },
  {
    key: 'mechanism',
    header: 'How',
    width: '18%',
    render: (r) => (
      <span className={r.mechanism === 'inline style' ? 'text-[var(--mustard)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
        {r.mechanism}
      </span>
    ),
  },
  { key: 'why', header: 'Why' },
];

export const PrioritiesAIFoundationsPage: React.FC = () => (
  <>
    <ProductDesignPageHeader
      title="Foundations"
      eyebrow="Rules · load-bearing"
      subtitle={
        <>
          Four rules that are easy to break silently. Each has been violated by code that looked
          correct and passed lint, typecheck and build — because the failure is visual and shows up
          only in one theme. Until now they existed only as comments in{' '}
          <code className="font-mono text-[0.85em] text-[var(--mustard)]">mono/primitives.ts</code>{' '}
          and <code className="font-mono text-[0.85em] text-[var(--mustard)]">ds/foundation.ts</code>,
          which is to say: only where the person about to break them would not look.
        </>
      }
    />

    <ProductDesignSection
      title="1 · The cascade trap"
      hint="A plain class loses in dark. This is the one that has cost the most time."
    >
      <Callout tone="coral" label="The failure" title="A semantic border renders grey in dark mode">
        <code className="font-mono text-[0.85em]">src/index.css</code> has two{' '}
        <strong className="text-[var(--text-pri)]">hand-written</strong> dark rules, and both come
        after Tailwind’s plain classes in the built sheet. Specificity decides, then order. So a
        plain <code className="font-mono text-[0.85em]">border-[var(--mustard)]</code> ties with{' '}
        <code className="font-mono text-[0.85em]">.dark *</code> and loses by coming first, and a
        plain text class on a heading is outweighed. An inline style wins in both themes. A{' '}
        <code className="font-mono text-[0.85em]">dark:</code> class also wins, in dark only.
      </Callout>

      <pre className="mt-5 overflow-x-auto border bg-[var(--bg-raised)] p-4 font-mono text-[13px] leading-relaxed text-[var(--text-sec)]"
           style={{ borderColor: 'var(--rule)' }}>
{`.dark *      { border-color: var(--rule) }
.dark h1…h6  { color: var(--text-pri) }`}
      </pre>

      <div className="mt-5">
        <DataTable
          columns={CASCADE_COLUMNS}
          rows={CASCADE_ROWS}
          getRowKey={(r) => r.property}
          density="compact"
          caption="Which mechanism to use for which property"
          captionHidden
        />
      </div>

      <p className="mt-5 text-sm leading-relaxed text-[var(--text-sec)]">
        Components in <code className="font-mono text-[0.85em] text-[var(--mustard)]">ds/</code>{' '}
        sidestep the border property entirely for accents, drawing them as{' '}
        <code className="font-mono text-[0.85em]">box-shadow: inset 3px 0 0</code> — see{' '}
        <code className="font-mono text-[0.85em]">accentShadow()</code> in{' '}
        <code className="font-mono text-[0.85em]">ds/foundation.ts</code>. A shadow has no cascade
        conflict, so the accent survives both themes without an inline style at every call site.
      </p>
    </ProductDesignSection>

    <ProductDesignSection
      title="2 · A bordered box means “you can press this”"
      hint="Status is read, not pressed — so it must not borrow the button's affordance."
    >
      <div className="flex flex-wrap items-center gap-8">
        <div>
          <p className="mb-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--green)] dark:text-[var(--text-ter)]">
            Status — no box
          </p>
          <div className="flex items-center gap-4">
            <span {...status('green')}>Active</span>
            <span {...status('coral')}>Blocked</span>
            <span {...status('mustard')}>Awaiting</span>
          </div>
        </div>
        <div>
          <p className="mb-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
            Chip — boxed value
          </p>
          <div className="flex items-center gap-2">
            <span {...chip('slate')}>Division</span>
            <span {...chip('neutral')}>FY26 Q3</span>
          </div>
        </div>
        <div>
          <p className="mb-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">
            Button — pressable
          </p>
          <button type="button" {...btnSecondary()}>Archive</button>
        </div>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-[var(--text-sec)]">
        Before this split existed, a green “Active” pill was visually identical to the button beside
        it, and nothing on screen told you which one did something. Use{' '}
        <code className="font-mono text-[0.85em] text-[var(--mustard)]">status()</code> for state,{' '}
        <code className="font-mono text-[0.85em] text-[var(--mustard)]">chip()</code> only for a
        tagged value or something genuinely pressable.
      </p>
    </ProductDesignSection>

    <ProductDesignSection
      title="3 · Controls are h-9, so they line up with buttons"
      hint="Matched to BTN_BASE. A field and a button side by side align without per-site nudging."
    >
      <div className="flex flex-wrap items-end gap-3">
        <input
          readOnly
          value="h-9 field"
          className="h-9 border bg-[var(--surface)] px-3 text-sm text-[var(--text-pri)]"
          style={{ borderColor: 'var(--rule-bright)' }}
        />
        <button type="button" {...btnSecondary()}>h-9 button</button>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-[var(--text-sec)]">
        Use <code className="font-mono text-[0.85em] text-[var(--mustard)]">size="sm"</code> (h-8)
        for dense rows. Never a custom height — the moment one control is 38px, every neighbour
        needs a margin nobody will maintain.
      </p>
    </ProductDesignSection>

    <ProductDesignSection
      title="4 · Checkbox vs Switch is semantic, not stylistic"
      hint="The components enforce the distinction by what they accept."
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--mustard)]" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-[var(--text-sec)]">
          A <strong className="text-[var(--text-pri)]">Switch</strong> takes effect the moment it is
          flipped — which is why it has no indeterminate state and no group. If flipping it does not
          do something immediately, it is a{' '}
          <strong className="text-[var(--text-pri)]">Checkbox</strong>: a value you are submitting as
          part of a form or a selection, which is why <em>that</em> one has an indeterminate state
          and belongs in a <code className="font-mono text-[0.85em]">ChoiceGroup</code>.
        </p>
      </div>
    </ProductDesignSection>
  </>
);
